// HomeShowcaseList.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Swipeable, { DIRECTION_LEFT } from '../component/Swipeable.jsx';
import WithIntersectionObserver from '../component/WithIntersectionObserver.jsx';
import HomeCoverMessage from '../container/HomeCoverMessage.js';
import {
  showcaseContainerGap,
  showcaseItemGap,
  showcaseMobileWidth,
  showcaseMobileHeight,
  showcaseMobileMinimumItemAmount,
  showcaseSwipeTransitionSeconds,
} from '../style/variables.js';
import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import prevIcon from '../../img/ic-white-prev.svg';
import nextIcon from '../../img/ic-white-next.svg';

const WINDOW_IDLE_DELAY_MSEC = 1000;
const windowIdleEvents = ['mousemove', 'click', 'scroll'];

export class HomeShowcaseList extends React.PureComponent {
  state = {
    isSwiping: false,
    isTransitionStart: false,
    deltaX: 0,
    hasWindowIdle: false,
  };

  deltaX = 0;
  frameId = null;
  transitionTimer = null;
  windowIdleTimeout = null;
  windowIdleCallback = null;

  changeIndex = ({ nextIndex }) => {
    const { changeActiveIndex, showcaseFeed } = this.props;

    changeActiveIndex({ activeIndex: nextIndex, type: showcaseFeed });
  };

  setWindowIdle = () => {
    this.clearWindowIdleSideEffects();
    this.setState({ hasWindowIdle: true });
  };
  clearWindowIdleSideEffects = () => {
    windowIdleEvents.forEach(event => {
      document.removeEventListener(event, this.setWindowIdle);
    });
    cancelIdleCallback(this.windowIdleCallback);
    clearTimeout(this.windowIdleTimeout);
  };
  initWindowIdleTimeout = () => {
    this.windowIdleTimeout = setTimeout(
      this.setWindowIdle,
      WINDOW_IDLE_DELAY_MSEC
    );
  };

  frameHandler = () => {
    this.setState({ deltaX: this.deltaX });
  };
  handleSwipeStart = () => {
    if (this.isTransitionStart) return;
    this.setState({
      isSwiping: true,
    });
  };
  handleSwiping = ({ deltaX, direction }) => {
    if (this.isTransitionStart) return;
    cancelAnimationFrame(this.frameId);

    this.deltaX = deltaX;
    this.frameId = requestAnimationFrame(this.frameHandler);

    if (direction !== this.state.direction) {
      return this.setState({ direction });
    }
  };
  handleSwipeEnd = ({ velocityX }) => {
    if (this.isTransitionStart) return;
    const { activeIndex, itemWidth } = this.props;
    const { deltaX, direction } = this.state;
    const nextIndex =
      direction === DIRECTION_LEFT ? activeIndex + 1 : activeIndex - 1;
    const shouldChangeIndex =
      Math.abs(deltaX) >= (itemWidth + showcaseItemGap) / 2 || velocityX >= 0.5;

    this.setState({
      isSwiping: false,
      isTransitionStart: true,
    });
    this.isTransitionStart = true;
    if (shouldChangeIndex) {
      const deltaX =
        (direction === DIRECTION_LEFT ? -1 : 1) * (itemWidth + showcaseItemGap);

      this.deltaX = deltaX;
      this.frameId = requestAnimationFrame(this.frameHandler);
      this.transitionTimer = setTimeout(() => {
        this.changeIndex({ nextIndex });
        this.isTransitionStart = false;
      }, showcaseSwipeTransitionSeconds * 1000);
    } else {
      this.deltaX = 0;
      this.frameId = requestAnimationFrame(this.frameHandler);
      this.transitionTimer = setTimeout(() => {
        this.setState({
          isTransitionStart: false,
        });
        this.isTransitionStart = false;
      }, showcaseSwipeTransitionSeconds * 1000);
    }
  };
  handleClickItemWrapper = ({ index, item }) => {
    const { indexes, activeIndex, itemWidth } = this.props;
    const { isSwiping, isTransitionStart } = this.state;

    if (isSwiping || isTransitionStart) {
      return;
    }

    const isActive = activeIndex === item;

    if (!isActive) {
      const middleIndexInListing = Math.floor(indexes.length / 2);
      const deltaX =
        (index - middleIndexInListing) * (itemWidth + showcaseItemGap) * -1;

      this.setState({
        isTransitionStart: true,
        deltaX,
      });
      this.transitionTimer = setTimeout(() => {
        this.changeIndex({ nextIndex: item });
      }, showcaseSwipeTransitionSeconds * 1000);
    }
  };
  handleChangeIndexByIndexDelta = ({ deltaIndex }) => {
    const { itemWidth, activeIndex } = this.props;
    const deltaX = (itemWidth + showcaseItemGap) * deltaIndex * -1;

    this.setState({
      isTransitionStart: true,
      deltaX,
    });
    this.transitionTimer = setTimeout(() => {
      this.changeIndex({
        nextIndex: activeIndex + deltaIndex,
      });
    }, showcaseSwipeTransitionSeconds * 1000);
  };
  handleResize = () => {
    this.props.changeShowcaseSize();
  };
  componentDidMount() {
    const { fetchFeeds, showcaseFeed } = this.props;

    fetchFeeds({ type: showcaseFeed, page: 1, limit: 100 });

    this.handleResize();
    window.addEventListener('resize', this.handleResize);

    this.windowIdleCallback = requestIdleCallback(this.initWindowIdleTimeout);
    windowIdleEvents.forEach(event => {
      document.addEventListener(event, this.setWindowIdle);
    });
  }
  componentDidUpdate(prevProps) {
    const {
      activeIndex: prevActiveIndex,
      isAuthed: prevIsAuthed,
      shouldRefresh: prevShouldRefresh,
      showcaseFeed: prevShowcaseFeed,
    } = prevProps;
    const { activeIndex, isAuthed, shouldRefresh, fetchFeeds, showcaseFeed } =
      this.props;
    if (activeIndex !== prevActiveIndex) {
      this.setState({
        isTransitionStart: false,
        deltaX: 0,
      });
    }

    if (
      isAuthed !== prevIsAuthed ||
      showcaseFeed !== prevShowcaseFeed ||
      (shouldRefresh && shouldRefresh !== prevShouldRefresh)
    ) {
      fetchFeeds({
        type: showcaseFeed,
        page: 1,
        limit: 100,
        shouldSkipLocalCache: shouldRefresh,
      });
    }
  }
  componentWillUnmount() {
    this.clearWindowIdleSideEffects();

    window.removeEventListener('resize', this.handleResize);

    clearTimeout(this.transitionTimer);

    cancelAnimationFrame(this.frameId);
  }
  render() {
    const { deltaX, isSwiping, isTransitionStart, hasWindowIdle } = this.state;
    const { itemIds, indexes, activeIndex, itemAmount, itemWidth, itemHeight } =
      this.props;
    const listingWidth =
      itemAmount * itemWidth +
      (itemAmount - 1) * showcaseItemGap +
      showcaseContainerGap * 2;

    return (
      <StyledHomeShowcaseList>
        <NavigationButton
          type="prev"
          onClick={() => {
            this.handleChangeIndexByIndexDelta({ deltaIndex: -1 });
          }}
        >
          <NavigationIcon src={prevIcon} alt="previous button" />
        </NavigationButton>
        <SwipeableWrapper>
          <Swipeable
            onSwipeStart={this.handleSwipeStart}
            onSwiping={this.handleSwiping}
            onSwipeEnd={this.handleSwipeEnd}
          >
            <ShowcaseList
              deltaX={deltaX}
              listingWidth={listingWidth}
              hasTransition={isTransitionStart}
            >
              {indexes.map((item, index) => {
                const isActive = activeIndex === item.index;
                const messageId = itemIds[item.index];
                const key = item.key;

                return (
                  <WithIntersectionObserver
                    threshold={0.5}
                    shouldKeepObserve
                    key={key}
                  >
                    {({ isIntersecting }) => {
                      return (
                        <HomeCoverMessageWrapper
                          itemWidth={itemWidth}
                          itemHeight={itemHeight}
                          onMouseDown={event => {
                            event.preventDefault();
                          }}
                          onClick={() =>
                            this.handleClickItemWrapper({
                              index,
                              item: item.index,
                            })
                          }
                        >
                          <HomeCoverMessage
                            index={activeIndex}
                            category={'showcase'}
                            messageId={messageId}
                            isIntersecting={isIntersecting}
                            isActive={isActive}
                            hasWindowIdle={hasWindowIdle}
                            isUIInteractable={!isSwiping && !isTransitionStart}
                          />
                        </HomeCoverMessageWrapper>
                      );
                    }}
                  </WithIntersectionObserver>
                );
              })}
            </ShowcaseList>
          </Swipeable>
        </SwipeableWrapper>
        <NavigationButton
          type="next"
          onClick={() => {
            this.handleChangeIndexByIndexDelta({ deltaIndex: 1 });
          }}
        >
          <NavigationIcon src={nextIcon} alt="next button" />
        </NavigationButton>
      </StyledHomeShowcaseList>
    );
  }
}

HomeShowcaseList.propTypes = {
  itemIds: PropTypes.array,
  indexes: PropTypes.array,
  activeIndex: PropTypes.number,
  itemWidth: PropTypes.number,
  itemHeight: PropTypes.number,
  itemAmount: PropTypes.number,
  isAuthed: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  changeActiveIndex: PropTypes.func,
  changeShowcaseSize: PropTypes.func,
  fetchFeeds: PropTypes.func,
  showcaseFeed: PropTypes.string,
};

HomeShowcaseList.defaultProps = {
  itemIds: [],
  indexes: [],
  activeIndex: 0,
  itemWidth: showcaseMobileWidth,
  itemHeight: showcaseMobileHeight,
  itemAmount: showcaseMobileMinimumItemAmount,
  isAuthed: false,
  shouldRefresh: false,
  changeShowcaseSize: () => null,
  changeActiveIndex: () => null,
  fetchFeeds: () => null,
  showcaseFeed: '',
};

const StyledHomeShowcaseList = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const NavigationButton = styled.button.attrs(({ type }) => ({
  style: {
    left: type === 'prev' ? 0 : 'auto',
    right: type === 'next' ? 0 : 'auto',
  },
}))`
  ${ResetButtonStyle}
  position: absolute;
  z-index: 1;
  top: 0px;
  width: 90px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  &[type='prev'] {
    background-image: linear-gradient(
      to left,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.6) 100%
    );
    & > img {
      margin-right: 10px;
    }
    &:hover {
      background-image: linear-gradient(
        to left,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.9) 100%
      );
    }
  }
  &[type='next'] {
    background-image: linear-gradient(
      to right,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.6) 100%
    );
    & > img {
      margin-left: 10px;
    }
    &:hover {
      background-image: linear-gradient(
        to right,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.9) 100%
      );
    }
  }
  & > img {
    opacity: 0.5;
  }
  :hover {
    & > img {
      opacity: 1;
    }
  }
  ${media.mobile`
    display: none;
  `}
`;

const NavigationIcon = styled.img`
  width: 48px;
  height: 48px;
`;

const SwipeableWrapper = styled.div`
  overflow: hidden;
`;

const ShowcaseList = styled.div.attrs(
  ({ deltaX, listingWidth, hasTransition }) => ({
    style: {
      transform: `translate3d(calc((100vw - ${listingWidth}px) * 0.5 + ${deltaX}px), 0px, 0px)`,
      transition: `transform ${
        hasTransition ? `${showcaseSwipeTransitionSeconds}s` : '0s'
      }`,
      width: `${listingWidth}px`,
    },
  })
)`
  display: flex;
  flex-wrap: nowrap;
  will-change: transform;
  ::before {
    content: '';
    width: ${showcaseContainerGap / 2}px;
    flex: none;
  }
  ::after {
    content: '';
    width: ${showcaseContainerGap / 2}px;
    flex: none;
  }
`;

const HomeCoverMessageWrapper = styled.div.attrs(
  ({ itemWidth, itemHeight }) => ({
    style: {
      width: `${itemWidth}px`,
      height: `${itemHeight}px`,
    },
  })
)`
  margin-left: ${showcaseItemGap / 2}px;
  margin-right: ${showcaseItemGap / 2}px;
`;

export default HomeShowcaseList;
